import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";

export function Hero() {
  return (
    <section className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28">
      {/* The page-wide grid + teal top glow comes from the global backdrop in
          App.tsx; the hero just adds a soft mesh for a little extra depth. */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-mesh opacity-40" />

      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <h1 className="font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-6xl md:text-7xl">
            RentOS for{" "}
            <span className="gradient-text animate-gradient">self-drive rentals.</span>
          </h1>

          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Replace your paper register, WhatsApp group and UPI screenshots with one
            structured workflow. Bookings, pickup, live trips, billing, deposits, KYC and
            reports — for owner-run bike, scooter and car rentals in India.
          </p>

          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 rounded-xl bg-gradient-primary px-5 py-3 text-sm font-medium text-primary-foreground shadow-glow transition-transform hover:-translate-y-0.5"
            >
              Book a demo
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/features"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-surface/50 px-5 py-3 text-sm font-medium text-foreground backdrop-blur transition-colors hover:bg-surface"
            >
              See the platform
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
            {["Free tier available", "One owner per business", "Export your data"].map((t) => (
              <li key={t} className="inline-flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5 text-primary" /> {t}
              </li>
            ))}
          </ul>
        </div>

        {/* product preview — single, cinematic */}
        <ProductMockup />
      </div>
    </section>
  );
}

function ProductMockup() {
  return (
    <div className="relative mx-auto mt-16 max-w-5xl">
      <div className="absolute -inset-x-20 -inset-y-12 -z-10 bg-gradient-mesh opacity-60 blur-3xl" />
      <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-elegant">
        {/* browser chrome */}
        <div className="flex items-center justify-between border-b border-border bg-background/60 px-4 py-2.5">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-destructive/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-yellow-500/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-primary/70" />
          </div>
          <div className="rounded-md border border-border bg-background/60 px-3 py-0.5 text-[10px] text-muted-foreground sm:text-xs">
            app.rentos.tech · Illustrative preview
          </div>
          <div className="hidden text-[10px] text-muted-foreground sm:block">Sample data</div>
        </div>

        {/* app body */}
        <div className="grid grid-cols-12 gap-0">
          {/* sidebar */}
          <aside className="col-span-3 hidden border-r border-border bg-background/40 p-4 sm:block">
            {["Dashboard", "Bookings", "Vehicles", "Customers", "Payments", "Reports", "Settings"].map((n, i) => (
              <div
                key={n}
                className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs ${
                  i === 0 ? "bg-primary/15 text-foreground" : "text-muted-foreground"
                }`}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-current opacity-60" />
                {n}
              </div>
            ))}
          </aside>

          {/* main */}
          <div className="col-span-12 p-5 sm:col-span-9 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Example business · Sample records</p>
                <h3 className="font-display text-lg font-semibold">Operations dashboard</h3>
              </div>
              <div className="hidden gap-2 sm:flex">
                <div className="rounded-md border border-border px-2 py-1 text-[10px] text-muted-foreground">Last 7 days</div>
                <div className="rounded-md bg-gradient-primary px-2.5 py-1 text-[10px] font-medium text-primary-foreground">+ Booking</div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { l: "Revenue", v: "₹1,48,210", d: "Recorded income" },
                { l: "Active trips", v: "23", d: "4 overdue" },
                { l: "Utilisation", v: "92%", d: "fleet of 48" },
                { l: "Deposits held", v: "₹62,400", d: "11 bookings" },
              ].map((k) => (
                <div key={k.l} className="rounded-xl border border-border bg-background/50 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k.l}</div>
                  <div className="mt-1 font-display text-base font-semibold sm:text-lg">{k.v}</div>
                  <div className="mt-0.5 text-[10px] text-primary">{k.d}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-xl border border-border bg-background/40 p-4">
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Revenue · last 14 days</div>
                  <div className="mt-0.5 font-display text-lg font-semibold">₹14.8L</div>
                </div>
                <div className="text-[10px] text-muted-foreground">Rental · Penalty · Deductions</div>
              </div>
              <svg viewBox="0 0 600 130" className="mt-3 h-24 w-full">
                <defs>
                  <linearGradient id="g" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="oklch(0.74 0.14 175)" stopOpacity=".55" />
                    <stop offset="100%" stopColor="oklch(0.74 0.14 175)" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0,100 C40,90 80,60 120,65 C160,70 200,40 240,45 C280,50 320,80 360,70 C400,60 440,30 480,38 C520,46 560,20 600,28 L600,130 L0,130 Z"
                  fill="url(#g)"
                />
                <path
                  d="M0,100 C40,90 80,60 120,65 C160,70 200,40 240,45 C280,50 320,80 360,70 C400,60 440,30 480,38 C520,46 560,20 600,28"
                  fill="none"
                  stroke="oklch(0.82 0.16 165)"
                  strokeWidth="2"
                />
              </svg>
            </div>

            <div className="mt-3 hidden overflow-hidden rounded-xl border border-border sm:block">
              <table className="w-full text-xs">
                <thead className="bg-background/60 text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-normal">Booking</th>
                    <th className="px-3 py-2 text-left font-normal">Customer</th>
                    <th className="px-3 py-2 text-left font-normal">Vehicle</th>
                    <th className="px-3 py-2 text-left font-normal">Status</th>
                    <th className="px-3 py-2 text-right font-normal">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {[
                    ["#B-1042", "Rohan Kale", "Activa 6G · MH12·QR04", "Out for rental", "₹1,840"],
                    ["#B-1041", "Niharika S.", "Swift Dzire · GA01·BT19", "Overdue · 42m", "₹4,210"],
                    ["#B-1039", "Aman Khan", "Pulsar NS200 · MH14·KZ02", "Returned", "₹2,460"],
                  ].map((r) => (
                    <tr key={r[0]} className="bg-background/30">
                      {r.map((c, i) => (
                        <td
                          key={i}
                          className={`px-3 py-2 ${i === 4 ? "text-right" : ""} ${
                            i === 3 && c.startsWith("Overdue") ? "text-destructive" : i === 3 ? "text-primary" : "text-foreground"
                          }`}
                        >
                          {c}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
